import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { Session } from '@cribbage/protocol';
import { ApiError, api } from '~/api/client.js';
import { type Departing, useAnonymous, useAuthStore } from '~/store/authStore.js';
import { type FriendlyError, toFriendlyError } from '~/store/sessionStore.js';
import { Avatar } from '~/ui/Avatar.js';
import { Banner } from '~/ui/Banner.js';
import { ButtonLink } from '~/ui/Button.js';
import { LoadingLine } from '~/ui/LoadingLine.js';
import { Panel } from '~/ui/Panel.js';
import { Section } from '~/ui/Section.js';
import { TextField } from '~/ui/TextField.js';
import type { AccountFlash } from './AccountPage.js';
import { ACCOUNT_PATH, PROFILE_PATH, openedFromProfile, returnToChip } from './accountPages.js';
import { SubmitButton } from './SubmitButton.js';

/**
 * The confirmation Delete opens, drawn to `docs/ux/account/delete-account.md`.
 *
 * Who is being deleted sits above an untitled panel and names it. The panel
 * says what goes and how that differs from signing out, and its one chip is
 * the way back, in lemon. The final action sits in its own labeled group
 * behind a typed word: the destructive chip is white and stays off until the
 * word is typed, which is where the care is, not in a color.
 *
 * A guest gets the same page for their guest data (D19), as the sketch's guest
 * frame draws it: no sign-out sentence, because a guest has nothing to sign
 * out of, and a line on what other players keep. The server answers a guest's
 * deletion with a fresh guest for this install, which the page hands to the
 * store, so the device is never left without a player.
 *
 * A dropped connection is not a refusal. The request may have landed, so the
 * page asks the server whether the account is still there before it says
 * anything: gone means deleted, still there means nothing was, and no answer
 * at all means the page cannot know yet and says only that.
 */

type Failure = FriendlyError | 'notDeleted';

export function DeleteAccountPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Keeping the account goes back to whichever screen opened this one.
  const fromProfile = openedFromProfile(useLocation().state);
  const whom = useId();
  const user = useAuthStore((state) => state.user);
  const ready = useAuthStore((state) => state.ready);
  const anonymous = useAnonymous();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  // A launch has no player until `/me` answers, so a reload or a typed URL
  // lands here before there is anyone to judge. Wait for the answer rather
  // than send an account holder back to Account.
  if (user === null && !ready && !done) return <LoadingLine />;

  // Nobody to delete. The page is also left behind the moment a deletion
  // lands, and that departure carries its own flash.
  if (user === null && !done) return <Navigate to={ACCOUNT_PATH} replace />;

  const guest = anonymous;

  const word = t('settings.account.deleteWord');
  const confirmed = typed.trim() === word;

  /**
   * The server has taken the deletion, or the page cannot tell whether it has:
   * either way this device leaves the account. What the page says afterwards
   * is only what it knows.
   */
  const leave = async (
    departing: Departing,
    serverTookIt: boolean,
    session?: Session,
  ): Promise<void> => {
    setDone(true);
    void navigate(ACCOUNT_PATH, {
      replace: true,
      state: { flash: await departure(departing, serverTookIt, session) },
    });
  };

  const departure = async (
    departing: Departing,
    serverTookIt: boolean,
    session?: Session,
  ): Promise<AccountFlash> => {
    const auth = useAuthStore.getState();
    if (guest) {
      await auth.leaveErasedGuest(departing, serverTookIt ? session : undefined);
      return serverTookIt ? 'guestDeleted' : 'guestDeleteUnconfirmed';
    }
    const playing = await auth.leaveDeletedAccount(departing, serverTookIt);
    if (!serverTookIt) return 'deleteUnconfirmed';
    return playing ? 'deleted' : 'deletedOffline';
  };

  const destroy = async (departing: Departing): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await leave(departing, true, await api.deleteAccount());
      return;
    } catch (error) {
      const refused = toFriendlyError(error);
      if (refused.key !== 'errors.network') {
        setFailure(refused);
        setBusy(false);
        return;
      }
    }
    /*
     * The request may have landed. A session that still works means the
     * player is still there, so nothing was deleted. A 401 means this session
     * is over, but not why: a deletion that landed, or a session that had
     * already expired or been revoked. The page leaves the player either way
     * and does not claim the deletion it cannot see.
     */
    try {
      await api.me();
      setFailure('notDeleted');
    } catch (check) {
      if (check instanceof ApiError && check.isAuthFailure) {
        await leave(departing, false);
        return;
      }
      setFailure(toFriendlyError(check));
    }
    setBusy(false);
  };

  return (
    <>
      <div id={whom} className="flex items-center gap-3">
        <Avatar kind="you" side="rows" size="band" />
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-extrabold">{user?.display_name}</span>
          {user?.email != null && <span className="truncate text-muted">{user.email}</span>}
        </div>
      </div>
      <Panel aria-labelledby={whom}>
        <p className="text-muted">
          {t(guest ? 'settings.account.deleteGuestBody' : 'settings.account.deleteBody')}
        </p>
        <p className="text-muted">
          {t(guest ? 'settings.account.deleteGuestOthers' : 'settings.account.deleteVsSignOut')}
        </p>
        <ButtonLink
          variant="primary"
          to={fromProfile ? PROFILE_PATH : ACCOUNT_PATH}
          replace
          state={returnToChip('delete')}
        >
          {t(guest ? 'settings.account.keepGuest' : 'settings.account.keepAccount')}
        </ButtonLink>
      </Panel>
      <Section title={t('settings.account.deleteForGood')}>
        <form
          className="flex flex-col gap-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            const { token } = useAuthStore.getState();
            if (confirmed && !busy && user !== null) void destroy({ token, playerId: user.id });
          }}
        >
          <TextField
            label={t('settings.account.typeDelete')}
            hint={t('settings.account.typeDeleteHint')}
            type="text"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
          />
          {failure !== null && (
            <Banner role="alert">
              {failure === 'notDeleted' ? t('settings.account.deleteNotReached') : t(failure.key)}
            </Banner>
          )}
          <div className="flex justify-end">
            <SubmitButton
              variant="default"
              busy={busy}
              busyLabel={t('settings.account.deleting')}
              disabled={!confirmed}
            >
              {t(guest ? 'settings.account.deleteGuestNow' : 'settings.account.deleteNow')}
            </SubmitButton>
          </div>
        </form>
      </Section>
    </>
  );
}
